import {
  Check,
  CheckCircle2,
  ChevronRight,
  Database,
  GitBranch,
  Globe,
  Lock,
  Plus,
  Trash2,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { schemaOwnership } from '@/features/schema/case-binding'
import { representativeSchema } from '@/features/schema/format'
import type { SchemaGroup } from '@/features/schema/types'
import type { Schema } from '@/services/api/schemas'
import { SchemaMetaChips } from './SchemaHero'

/** Shared Global/Case ownership badge — used on cards and the in-use banner. */
export function OwnershipBadge({ schema }: { schema: Pick<Schema, 'case_id'> }) {
  const isGlobal = schemaOwnership(schema) === 'global'
  return (
    <Badge variant="outline" className="text-xs border-border gap-1">
      {isGlobal ? <Globe className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
      {isGlobal ? 'Global' : 'Case'}
    </Badge>
  )
}

/** The leading "+ create new" gallery card, shown only in case mode. */
export function CreateSchemaCard({ onClick }: { onClick: () => void }) {
  return (
    <Card
      interactive
      onClick={onClick}
      className="bg-card/20 border-dashed transition-colors hover:border-primary/50"
    >
      <CardContent className="flex h-full min-h-32 flex-col items-center justify-center gap-2 text-center">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-primary/40 bg-primary/5">
          <Plus className="h-5 w-5 text-primary" />
        </div>
        <span className="font-medium text-foreground">Create case schema</span>
        <span className="text-xs text-muted-foreground">Seeded with a Person entity</span>
      </CardContent>
    </Card>
  )
}

/**
 * One schema (its latest version is the representative) as a gallery card. Shared
 * by the admin grid and the case-view sections. In case mode it surfaces the
 * "Global default" / "In use" markers and a "Use" action; in admin mode it shows
 * the "Active" dot instead.
 */
export function GalleryCard({
  group,
  onOpen,
  onDelete,
  caseMode = false,
  isInUse = false,
  isGlobalDefault = false,
  onUse,
}: {
  group: SchemaGroup
  onOpen: (id: string) => void
  onDelete: (schema: Schema) => void
  /** Case-view extras — omit in admin mode (defaults render the "Active" dot). */
  caseMode?: boolean
  isInUse?: boolean
  isGlobalDefault?: boolean
  onUse?: (schemaId: string) => void
}) {
  const schema = representativeSchema(group)
  return (
    <Card
      interactive
      onClick={() => onOpen(schema.id)}
      className="bg-card/40 transition-colors hover:border-primary/40"
    >
      <CardHeader className="pb-2">
        <div className="flex max-md:min-w-0 items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Database className="h-4 w-4 text-primary shrink-0" />
            <span className="font-medium text-foreground truncate max-md:line-clamp-2 max-md:whitespace-normal max-md:break-words">
              {group.name}
            </span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {caseMode ? (
              <>
                <OwnershipBadge schema={schema} />
                {isGlobalDefault && (
                  <Badge variant="outline" className="text-xs border-border">
                    Global default
                  </Badge>
                )}
                {isInUse && (
                  <span className="inline-flex items-center gap-1 text-xs text-success-foreground">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    In use
                  </span>
                )}
              </>
            ) : (
              group.versions.some(v => v.is_active) && (
                <span className="inline-flex items-center gap-1 text-xs text-success-foreground">
                  <span className="h-1.5 w-1.5 rounded-full bg-success" />
                  Active
                </span>
              )
            )}
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={e => {
                e.stopPropagation()
                onDelete(schema)
              }}
              className="text-muted-foreground hover:text-destructive"
              title={`Delete ${group.name} v${schema.version}`}
              aria-label={`Delete ${group.name} v${schema.version}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground line-clamp-2 min-h-8">{schema.description}</p>
        <SchemaMetaChips schema={schema} />
        <div className="flex items-center justify-between pt-1">
          <Badge variant="outline" className="text-xs border-border tabular-nums">
            <GitBranch className="h-3 w-3 mr-1" />
            {group.versions.length} version{group.versions.length === 1 ? '' : 's'}
          </Badge>
          {caseMode && !isInUse ? (
            <Button
              variant="outline"
              size="sm"
              onClick={e => {
                e.stopPropagation()
                onUse?.(schema.id)
              }}
              className="h-7 border-border"
            >
              <Check className="h-3.5 w-3.5 mr-1" />
              Use
            </Button>
          ) : (
            <span className="inline-flex items-center text-xs text-muted-foreground">
              Open <ChevronRight className="h-3.5 w-3.5" />
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
